import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    newUrl: String,
    createUrl: String,
    selectReasonUrl: String,
  };

  static targets = ["studentDrawer", "reasonDrawer", "selectedStudent"];

  openStudentDrawer() {
    // Show blur overlay
    this.showBlurOverlay();

    // Load student selection drawer
    fetch(`${this.newUrlValue}?step=select_student`, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        // Insert the drawer HTML
        const content = document.getElementById("drawer_content");
        if (content) {
          content.innerHTML = html;
        }
      });
  }

  selectStudent(event) {
    const studentId = event.currentTarget.dataset.studentId;
    const studentName = event.currentTarget.dataset.studentName;

    // Create draft generation
    fetch(this.createUrlValue || `${this.newUrlValue}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify({
        radiation_certificate_generation: {
          student_id: studentId,
          status: "draft",
        },
      }),
    })
      .then((response) => response.text())
      .then((html) => {
        // Close student drawer and open reason drawer
        this.closeStudentDrawer();

        // Open reason selection
        setTimeout(() => {
          this.openReasonDrawer(studentId, studentName);
        }, 300);
      });
  }

  openReasonDrawer(studentId, studentName) {
    const url =
      this.selectReasonUrlValue ||
      `${this.newUrlValue}?step=select_reason&student_id=${studentId}`;

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const content = document.getElementById("drawer_content");
        if (content) {
          content.innerHTML = html;
        }
      });
  }

  selectReason(event) {
    const reason = event.currentTarget.dataset.reason;
    const generationId = event.currentTarget.dataset.generationId;

    // Load conditional form for this reason
    this.loadConditionalForm(reason, generationId);
  }

  loadConditionalForm(reason, generationId) {
    // Hide reason selection and show conditional form
    const reasonCards = document.querySelectorAll("[data-reason-card]");
    reasonCards.forEach((card) => {
      if (card.dataset.reason === reason) {
        // Expand this card and show form
        this.expandReasonCard(card, reason, generationId);
      } else {
        // Collapse other cards
        card.style.opacity = "0.5";
      }
    });
  }

  expandReasonCard(card, reason, generationId) {
    // Add expanded state
    card.classList.add("border-cta", "bg-blue-50");

    // Load conditional form
    const formContainer = card.querySelector("[data-conditional-form]");
    if (formContainer) {
      fetch(
        `${this.newUrlValue}?step=conditional_form&reason=${reason}&generation_id=${generationId}`,
        {
          headers: {
            Accept: "text/vnd.turbo-stream.html",
          },
        },
      )
        .then((response) => response.text())
        .then((html) => {
          formContainer.innerHTML = html;
          formContainer.classList.remove("hidden");
        });
    }
  }

  submitConditionalForm(event) {
    event.preventDefault();
    const form = event.target;

    // Removal of problematic manual flatpickr sync.
    // Flatpickr handles syncing to the hidden input automatically.
    // Overwriting input.value with input._flatpickr.input.value was causing
    // formatted dates (e.g. DD/MM/YYYY) to be sent instead of ISO dates (YYYY-MM-DD).

    const formData = new FormData(form);

    // Debug: Check if dates are present
    console.log(
      "Submitting form with data:",
      Object.fromEntries(formData.entries()),
    );

    fetch(form.action, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          .content,
        Accept: "text/vnd.turbo-stream.html",
      },
      redirect: "follow",
    }).then((response) => {
      if (response.redirected) {
        window.location.href = response.url;
        return;
      }
      return response.text().then((html) => {
        if (response.ok) {
          this.closeAllDrawers();
          window.location.reload();
        } else {
          // Process Turbo Stream if returned, otherwise manual update
          if (html.includes("turbo-stream")) {
            import("@hotwired/turbo")
              .then((Turbo) => {
                Turbo.renderStreamMessage(html);
              })
              .catch(() => {
                // Fallback if Turbo is not available via import
                if (window.Turbo) {
                  window.Turbo.renderStreamMessage(html);
                }
              });
          } else {
            const content = document.getElementById("drawer_content");
            if (content) {
              content.innerHTML = html;
            }
          }
        }
      });
    });
  }

  // Toggle priority treatment option
  togglePriority(event) {
    const checkbox = event.target;
    const icon = this.element.querySelector("[data-priority-icon]");
    const hiddenField = this.element.querySelector(
      'input[name="radiation_certificate_generation[priority_treatment]"]',
    );

    if (checkbox.checked) {
      icon.classList.remove("hidden");
      // Add priority class to form or container
      this.element.classList.add("priority-treatment");
      // Update hidden field value
      if (hiddenField) {
        hiddenField.value = "1";
      }
    } else {
      icon.classList.add("hidden");
      this.element.classList.remove("priority-treatment");
      // Update hidden field value
      if (hiddenField) {
        hiddenField.value = "0";
      }
    }
  }

  closeStudentDrawer() {
    console.log("RadiationCertificates#closeStudentDrawer called");
    const content = document.getElementById("drawer_content");
    if (content) {
      console.log("Clearing drawer_content innerHTML");
      content.innerHTML = "";
    }
  }

  closeReasonDrawer() {
    const content = document.getElementById("drawer_content");
    if (content) {
      content.innerHTML = "";
    }
  }

  closeAllDrawers() {
    console.log("RadiationCertificates#closeAllDrawers called");
    this.closeStudentDrawer();
    this.closeReasonDrawer();
    this.hideBlurOverlay();
    window.dispatchEvent(new CustomEvent("drawer:close"));
  }

  showBlurOverlay() {
    // Create blur overlay if it doesn't exist
    let overlay = document.getElementById("blur-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "blur-overlay";
      overlay.className =
        "fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-40";
      overlay.addEventListener("click", () => this.closeAllDrawers());
      document.body.appendChild(overlay);
    }
    overlay.classList.remove("hidden");
  }

  hideBlurOverlay() {
    const overlay = document.getElementById("blur-overlay");
    if (overlay) {
      overlay.classList.add("hidden");
    }
  }

  // Handle escape key
  connect() {
    console.log("RadiationCertificates controller connected");
    this.boundHandleEscape = this.handleEscape.bind(this);
    document.addEventListener("keydown", this.boundHandleEscape);
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleEscape);
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.closeAllDrawers();
    }
  }
}
